import React, { useState, useCallback, useEffect } from 'react';
import { Box, Tooltip } from '@mui/material';
import { Add } from '@mui/icons-material';
import { GridRowSelectionModel } from '@mui/x-data-grid/models/gridRowSelectionModel';
import { DataGridContainer, StandardActions } from '..';
import { ChangeRequest } from '@/api/bol/models';
import { useLogisticsObject } from '@/hooks';
import type { LogisticsObjectEntity } from '@/types/logisticsObject';
import { usePermissions, getActionTooltip } from '@/hooks';
import { createChangeRequestColumns } from './change-requests/ChangeRequestColumns';
import { CreateChangeRequestDialog } from './change-requests/CreateChangeRequestDialog';
import { PatchChangeRequestDialog } from './change-requests/PatchChangeRequestDialog';
import { useChangeRequestMembers } from '@/hooks/useChangeRequestMembers';
import { useChangeRequestActions } from '@/hooks/useChangeRequestActions';

interface ChangeRequestsListProps {
  logisticsObjectType: LogisticsObjectEntity;
  objectId: string;
  onObjectUpdated?: () => void | Promise<void>;
  onChangeRequestSelected?: (changeRequest: ChangeRequest | null) => void;
  selectedChangeRequestId?: string | null;
}

const ChangeRequestsList: React.FC<ChangeRequestsListProps> = ({
  logisticsObjectType,
  objectId,
  onObjectUpdated,
  onChangeRequestSelected,
  selectedChangeRequestId
}) => {
  const { can } = usePermissions();
  const {
    changeRequests,
    changeRequestsLoading,
    changeRequestsPagination,
    refetchChangeRequests,
    refetchObject,
    refetchMetadata,
    metadata
  } = useLogisticsObject(logisticsObjectType, objectId);

  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [patchDialogOpen, setPatchDialogOpen] = useState(false);
  const [selectedForPatch, setSelectedForPatch] = useState<ChangeRequest | null>(null);

  const currentRevision = metadata?.revision || 1;

  const {
    members,
    membersLoading,
    selectedMemberIdentifier,
    newChangeRequest,
    operationsJsonText,
    handleMemberChange,
    handleOperationsChange,
    fetchMembers,
    resetForm,
    setNewChangeRequest
  } = useChangeRequestMembers(currentRevision);

  const handleSuccess = useCallback(async () => {
    await Promise.all([
      refetchChangeRequests(),
      refetchObject(),
      refetchMetadata()
    ]);
    await onObjectUpdated?.();
  }, [refetchChangeRequests, refetchObject, refetchMetadata, onObjectUpdated]);

  const {
    creating,
    createError,
    patching,
    patchError,
    createChangeRequest,
    patchChangeRequest,
    setCreateError,
    setPatchError
  } = useChangeRequestActions({
    logisticsObjectType,
    objectId,
    onSuccess: handleSuccess
  });

  // Sync selection with parent control (controlled component pattern)
  useEffect(() => {
    if (selectedChangeRequestId !== undefined && selectedChangeRequestId !== selectedRowId) {
      setSelectedRowId(selectedChangeRequestId ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChangeRequestId]);

  const handleCopyToClipboard = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  }, []);

  const handleRowSelectionChange = useCallback((selectionModel: GridRowSelectionModel) => {
    const newRowId = selectionModel.length > 0 ? String(selectionModel[0]) : null;
    setSelectedRowId(newRowId);
    const selectedChangeRequest = newRowId
      ? changeRequests.find(cr => cr.id === newRowId) || null
      : null;
    onChangeRequestSelected?.(selectedChangeRequest);
  }, [changeRequests, onChangeRequestSelected]);

  const handlePaginationChange = useCallback((newPaginationModel: { page: number; pageSize: number }) => {
    refetchChangeRequests(newPaginationModel.page, newPaginationModel.pageSize);
  }, [refetchChangeRequests]);

  const handleCreateDialogOpen = useCallback(() => {
    setCreateError(null);
    resetForm(currentRevision);
    setCreateDialogOpen(true);
  }, [resetForm, currentRevision, setCreateError]);

  const handleCreateDialogClose = useCallback(() => {
    setCreateDialogOpen(false);
    setCreateError(null);
  }, [setCreateError]);

  const handleCreate = useCallback(async () => {
    const success = await createChangeRequest(newChangeRequest);
    if (success) {
      setCreateDialogOpen(false);
    }
  }, [createChangeRequest, newChangeRequest]);

  const handlePatchDialogOpen = useCallback((changeRequest: ChangeRequest) => {
    setSelectedForPatch(changeRequest);
    setPatchError(null);
    setPatchDialogOpen(true);
  }, [setPatchError]);

  const handlePatchDialogClose = useCallback(() => {
    setPatchDialogOpen(false);
    setPatchError(null);
    setSelectedForPatch(null);
  }, [setPatchError]);

  const handlePatch = useCallback(async (status: 'ACCEPTED' | 'REJECTED') => {
    if (!selectedForPatch) return;
    const success = await patchChangeRequest(selectedForPatch, status);
    if (success) {
      setPatchDialogOpen(false);
    }
  }, [selectedForPatch, patchChangeRequest]);

  const columns = createChangeRequestColumns(
    handleCopyToClipboard,
    handlePatchDialogOpen,
    can.updateLogisticsObject,
    patching
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <DataGridContainer
        rows={changeRequests}
        columns={columns}
        loading={changeRequestsLoading}
        selectedRowId={selectedRowId}
        onRowSelectionChange={handleRowSelectionChange}
        paginationModel={{
          page: changeRequestsPagination.page,
          pageSize: changeRequestsPagination.pageSize
        }}
        rowCount={changeRequestsPagination.total}
        onPaginationModelChange={handlePaginationChange}
        getRowId={(row) => row.id}
        sx={{ flex: 1 }}
        actionButtons={
          <>
            <Tooltip title={!can.updateLogisticsObject ? getActionTooltip('updateLogisticsObject') : ''}>
              <span>
                <StandardActions
                  onAdd={handleCreateDialogOpen}
                  addButtonText="Create Change Request"
                  addButtonIcon={<Add />}
                  disabled={!can.updateLogisticsObject}
                />
              </span>
            </Tooltip>
            <StandardActions
              onRefresh={() => refetchChangeRequests()}
              refreshTooltip="Refresh change requests list"
            />
          </>
        }
      />

      <CreateChangeRequestDialog
        open={createDialogOpen}
        onClose={handleCreateDialogClose}
        onCreate={handleCreate}
        changeRequest={newChangeRequest}
        onChangeRequest={(update) => setNewChangeRequest(prev => ({ ...prev, ...update }))}
        operationsJsonText={operationsJsonText}
        onOperationsChange={handleOperationsChange}
        members={members}
        membersLoading={membersLoading}
        selectedMemberIdentifier={selectedMemberIdentifier}
        onMemberChange={handleMemberChange}
        onRefreshMembers={fetchMembers}
        error={createError}
        creating={creating}
      />

      <PatchChangeRequestDialog
        open={patchDialogOpen}
        onClose={handlePatchDialogClose}
        onPatch={handlePatch}
        changeRequest={selectedForPatch}
        error={patchError}
        patching={patching}
      />
    </Box>
  );
};

export default ChangeRequestsList;
